import { ReportPageType, ReportSection } from '../constants/common';
import { escapeTocHtml, preserveTocSpacesForWord, buildWordNativeTocFieldHtml, buildWordMsoDocumentShell } from './reportTocWordHtml';

/** GRI section title color in stored HTML (green headings → Word Heading 1). */
const GRI_SECTION_GREEN = /rgb\(\s*45\s*,\s*194\s*,\s*107\s*\)|#2dc26b|2dc26b/i;

const GRI_WORD_SECTION_HEADING_STYLE =
  'mso-style-name:"Heading 1";mso-outline-level:1;font-size:18pt;font-weight:bold;color:#2dc26b;font-family:Arial,Helvetica,sans-serif;margin:12pt 0 6pt 0;mso-line-height-rule:exactly;';

const GRI_WORD_PAGE_HEADING_STYLE =
  'mso-style-name:"Heading 2";mso-outline-level:2;font-size:16pt;font-weight:bold;color:#1e293b;font-family:Arial,Helvetica,sans-serif;margin:10pt 0 4pt 0;mso-line-height-rule:exactly;';

const isGriSectionHeading = (el) => {
  const markup = `${el.getAttribute('style') || ''} ${el.innerHTML || ''}`;
  return GRI_SECTION_GREEN.test(markup);
};

/**
 * Word export helpers: GRI document assembly, heading normalization, and optional physical
 * page measurement. GRI TOC uses native Word field codes so page numbers are accurate after
 * Update Field in Microsoft Word.
 */
export default class ReportWordExportUtils {
  static measureRoot = null;

  static WORD_DOCX_GRI_HEADING_CSS = `
h1, h1.gri-word-section-heading {
  mso-style-name: "Heading 1";
  mso-style-link: "Heading 1 Char";
  mso-outline-level: 1;
  font-size: 18pt;
  font-weight: bold;
  color: #2dc26b;
  margin: 12pt 0 6pt 0;
  font-family: Arial, Helvetica, sans-serif;
}
h2, h2.gri-word-page-heading {
  mso-style-name: "Heading 2";
  mso-style-link: "Heading 2 Char";
  mso-outline-level: 2;
  font-size: 16pt;
  font-weight: bold;
  color: #1e293b;
  margin: 10pt 0 4pt 0;
  font-family: Arial, Helvetica, sans-serif;
}
`.trim();

  /** Map styled h4 section/page titles to Word Heading 1 / Heading 2 for html-docx export. */
  static prepareGriPageHtmlForWord(html) {
    if (!html || typeof html !== 'string') return html;

    const parser = new DOMParser();
    const doc = parser.parseFromString(`<div id="gri-word-export-root">${html}</div>`, 'text/html');
    const root = doc.getElementById('gri-word-export-root');
    if (!root) return html;

    root.querySelectorAll('h1, h2, h3, h4, h5, h6').forEach((el) => {
      const text = (el.textContent || '').trim();
      if (!text) {
        el.remove();
        return;
      }

      const sectionHeading = isGriSectionHeading(el);
      const tagName = sectionHeading ? 'h1' : 'h2';
      const heading = doc.createElement(tagName);
      heading.setAttribute(
        'class',
        sectionHeading ? 'gri-word-section-heading' : 'gri-word-page-heading',
      );
      heading.setAttribute(
        'style',
        sectionHeading ? GRI_WORD_SECTION_HEADING_STYLE : GRI_WORD_PAGE_HEADING_STYLE,
      );
      heading.textContent = text;
      el.replaceWith(heading);
    });

    return root.innerHTML;
  }

  static PAGE_PROFILE_A4 = {
    widthPx: 8.27 * 96,
    paddingTopPx: 50,
    paddingHorizontalPx: 50,
    paddingBottomPx: 50,
    footerPx: 60,
    pageHeightPx: 11.69 * 96,
    minPages: 1,
  };

  static buildPageKey(sectionIndex, pageIndex) {
    return `${sectionIndex}-${pageIndex}`;
  }

  static buildAnchorId(sectionIndex, pageIndex) {
    return `report-page-${sectionIndex}-${pageIndex}`;
  }

  static decodePageHtml(page) {
    if (page.html) return page.html;
    if (!page.htmlContent) return '';
    try {
      return decodeURIComponent(escape(window.atob(page.htmlContent)));
    } catch (e) {
      return '';
    }
  }

  static isCoverPage(page) {
    return page.type === ReportPageType.TITLE || page.type === 'TITLE' || page.type === 0;
  }

  static isBackCoverPage(page) {
    return page.sectionType === ReportSection.ROOT || page.sectionType === 'ROOT';
  }

  static isContentsPage(page) {
    return page.type === ReportPageType.CONTENTS || page.type === 'CONTENTS';
  }

  /** How many A4 pages a block of HTML will occupy when printed/exported. */
  static measurePhysicalPages(html, { forceSinglePage = false } = {}) {
    const profile = ReportWordExportUtils.PAGE_PROFILE_A4;
    if (forceSinglePage) return profile.minPages;
    if (!html?.trim()) return profile.minPages;

    if (typeof document === 'undefined') return profile.minPages;

    if (!ReportWordExportUtils.measureRoot) {
      ReportWordExportUtils.measureRoot = document.createElement('div');
      ReportWordExportUtils.measureRoot.setAttribute('aria-hidden', 'true');
      document.body.appendChild(ReportWordExportUtils.measureRoot);
    }

    const root = ReportWordExportUtils.measureRoot;
    root.style.cssText = `position:fixed;left:-10000px;top:0;width:${profile.widthPx}px;visibility:hidden;`;
    root.innerHTML = `<div style="box-sizing:border-box;padding:${profile.paddingTopPx}px ${profile.paddingHorizontalPx}px ${profile.paddingBottomPx}px;font-family:Inter,Arial,sans-serif;font-size:14px;line-height:1.5;">${html}</div>`;

    const usableHeight = profile.pageHeightPx - profile.paddingTopPx - profile.paddingBottomPx - profile.footerPx;
    const height = root.firstElementChild?.scrollHeight || 0;
    return Math.max(profile.minPages, Math.ceil(height / usableHeight));
  }

  /**
   * Maps each page key to its physical start page (cover = 1).
   * @param {Array<{ key, html, forceSinglePage? }>} pagesInOrder
   * @param {{ tocPageKey?, buildTocHtml? }} tocOptions
   */
  static computePhysicalPageMap(pagesInOrder, tocOptions = {}) {
    const { tocPageKey = null, buildTocHtml = null } = tocOptions;
    if (!pagesInOrder?.length) return new Map();

    let pageMap = new Map(pagesInOrder.map((page, i) => [page.key, i + 1]));

    for (let pass = 0; pass < 6; pass += 1) {
      let current = 1;
      const nextMap = new Map();

      pagesInOrder.forEach((page) => {
        nextMap.set(page.key, current);
        let html = page.html || '';
        if (tocPageKey && page.key === tocPageKey && buildTocHtml) {
          html = buildTocHtml(pageMap);
        }
        current += ReportWordExportUtils.measurePhysicalPages(html, {
          forceSinglePage: page.forceSinglePage,
        });
      });

      const stable = pagesInOrder.every((p) => pageMap.get(p.key) === nextMap.get(p.key));
      pageMap = nextMap;
      if (stable) break;
    }

    return pageMap;
  }

  /**
   * Word-only TOC: simple two-column table (title | page number), no leader dots.
   * @param {object} report
   * @param {string} title
   * @param {Map<string, number>} physicalPageMap
   * @param {(section: object) => boolean} shouldSkipSection
   */
  static buildWordTableOfContentsHtml(report, title, physicalPageMap, shouldSkipSection) {
    const titleEscaped = escapeTocHtml(title);
    const cellRight = 'padding:0;font-size:11pt;line-height:1.15;color:#1e293b;text-align:right;white-space:nowrap;mso-line-height-rule:exactly;';
    const sectionLeft = 'padding:3pt 0 0;font-size:11pt;line-height:1.15;font-weight:bold;color:#1e293b;mso-line-height-rule:exactly;';
    const sectionRight = 'padding:3pt 0 0;font-size:11pt;line-height:1.15;font-weight:bold;color:#1e293b;text-align:right;white-space:nowrap;mso-line-height-rule:exactly;';
    const pageLeft = 'padding:1pt 0 0 16pt;font-size:11pt;line-height:1.15;mso-line-height-rule:exactly;';

    let rows = '';
    const sections = [...(report?.sections || [])].sort(
      (a, b) => (a.sortOrder || 0) - (b.sortOrder || 0),
    );

    sections.forEach((section) => {
      if (shouldSkipSection?.(section)) return;

      const pages = [...(section.pages || [])].sort(
        (a, b) => (a.sortOrder || 0) - (b.sortOrder || 0),
      );
      if (!pages.length) return;

      const sectionName = preserveTocSpacesForWord(escapeTocHtml(section.name || ''));
      const firstPageNum = physicalPageMap.get(
        ReportWordExportUtils.buildPageKey(section.index, pages[0].index),
      );

      rows += `<tr>
        <td style="${sectionLeft}">${sectionName}</td>
        <td style="${sectionRight}">${firstPageNum ?? ''}</td>
      </tr>`;

      pages.forEach((page) => {
        const pageNum = physicalPageMap.get(
          ReportWordExportUtils.buildPageKey(section.index, page.index),
        );
        const pageName = preserveTocSpacesForWord(escapeTocHtml(page.name || ''));
        const anchorId = ReportWordExportUtils.buildAnchorId(section.index, page.index);

        rows += `<tr>
          <td style="${pageLeft}">
            <a href="#${anchorId}" style="color:#1d4ed8;text-decoration:none;">${pageName}</a>
          </td>
          <td style="${cellRight}">${pageNum ?? ''}</td>
        </tr>`;
      });
    });

    return `<div style="font-family:Arial,Helvetica,sans-serif;color:#1e293b;padding:0;">
      <h2 style="font-size:22pt;font-weight:700;margin:0 0 10pt;color:#0f172a;font-family:Georgia,'Times New Roman',serif;line-height:1.15;mso-line-height-rule:exactly;">${titleEscaped}</h2>
      <table width="100%" cellpadding="0" cellspacing="0" border="0" style="width:100%;border-collapse:collapse;mso-table-lspace:0pt;mso-table-rspace:0pt;table-layout:fixed;">
        <colgroup><col width="85%"/><col width="15%"/></colgroup>
        ${rows}
      </table>
    </div>`;
  }

  /** Build full GRI Word document HTML with native Word TOC field and page anchors. */
  static buildGriWordDocument(report, selectedPageIds, t) {
    const selected = new Set(selectedPageIds);
    const tocTitle = t('TableofContents.label');
    const pages = [];
    let tocPageKey = null;

    report.sections?.forEach((section) => {
      section.pages?.forEach((page) => {
        if (!selected.has(page.id)) return;

        const entry = {
          key: ReportWordExportUtils.buildPageKey(section.index, page.index),
          sectionIndex: section.index,
          pageIndex: page.index,
          type: page.type,
          sectionType: section.type,
          html: ReportWordExportUtils.decodePageHtml(page),
          forceSinglePage: ReportWordExportUtils.isCoverPage({ type: page.type })
            || section.type === ReportSection.ROOT
            || section.type === 'ROOT',
        };

        if (ReportWordExportUtils.isContentsPage(page)) {
          tocPageKey = entry.key;
        }

        if (!entry.forceSinglePage && entry.html) {
          entry.html = entry.html.replace(/<br\s*\/?>/g, '<p style="margin:0;line-height:1;"></p>');
          if (!ReportWordExportUtils.isContentsPage(page)) {
            entry.html = ReportWordExportUtils.prepareGriPageHtmlForWord(entry.html);
          }
        }

        pages.push(entry);
      });
    });

    const exportTocHtml = tocPageKey
      ? buildWordNativeTocFieldHtml(
        tocTitle,
        t('GriWordExport.tocUpdateHint.label'),
      )
      : '';

    let body = '';

    pages.forEach((page, index) => {
      const anchorId = ReportWordExportUtils.buildAnchorId(page.sectionIndex, page.pageIndex);
      const anchor = `<a id="${anchorId}" name="${anchorId}"></a>`;
      let html = page.key === tocPageKey ? exportTocHtml : page.html;

      if (ReportWordExportUtils.isCoverPage(page) && html) {
        html = html.replace(/<img([^>]*)>/gi, (match) => (
          /width\s*=/i.test(match) ? match : match.replace(/<img/, '<img width="250"')
        ));
      }

      const pageBlock = ReportWordExportUtils.isCoverPage(page)
        ? `<div class="word-cover-page"><div class="word-cover-content">${anchor}${html}</div></div>`
        : `${anchor}${html}`;

      const breakTag = index < pages.length - 1
        ? '<br clear="all" style="page-break-before:always"/>'
        : '';

      body += `<div>${pageBlock}${breakTag}</div>`;
    });

    const griWordCss = `
      .word-cover-page{position:relative;min-height:100%;padding:1in;background-color:#f8fafc;}
      .word-cover-page .word-cover-content{position:relative;z-index:1;}
      .word-cover-page img{width:250px;object-fit:contain;}
      ${ReportWordExportUtils.WORD_DOCX_GRI_HEADING_CSS}
    `.trim();

    return buildWordMsoDocumentShell(body, griWordCss);
  }
}
